import CONFIG from './CONFIG';
import { useEffect, useCallback, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import { useMetaApi } from './MetaApiProvider';

export const useFacebookSignup = (isEnabled) => {
  const { showAlert } = useAlert();
  const channelId = localStorage.getItem('channelId');
  const redirectUri = CONFIG.FACEBOOK_REDIRECT_URI;
  const { getWhatsAppConstants, metaConnection } = useMetaApi();

  const [data, setData] = useState(null);

  // 🔹 Load WhatsApp constants from backend
  useEffect(() => {
    if (!isEnabled || data) return;

    const fetchConfig = async () => {
      try {
        const data = await getWhatsAppConstants();
        setData({
          appId: data.app_id,
          configId: data.config_id,
          version: data.version || 'v24.0',
        });
      } catch (e) {
        showAlert('fail', 'Failed to fetch WhatsApp configuration');
      }
    };

    fetchConfig();
  }, [isEnabled]);

  // 🔹 Load Facebook SDK after config is loaded
  useEffect(() => {
    if (!data) return;
    if (typeof window === 'undefined') return;

    const initParams = {
      appId: data.appId,
      autoLogAppEvents: true,
      xfbml: true,
      version: data.version,
    };

    if (window.FB) {
      window.FB.init(initParams);
    } else {
      window.fbAsyncInit = function () {
        window.FB.init(initParams);
      };

      (function (d, s, id) {
        let js,
          fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        fjs.parentNode.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');
    }
  }, [data]);

  // 🔹 Handle Embedded Signup messages
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let connected = false;

    const handleMessage = async (event) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      )
        return;

      try {
        const data = JSON.parse(event.data);

        if (data.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH') {
          if (!connected) {
            connected = true;

            const code = data.data.code;
            await metaConnection(code, redirectUri, channelId);

            showAlert('success', 'WhatsApp signup completed!');
          }
        }
      } catch (e) {
        console.error('Error handling embedded signup message:', e);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [redirectUri, channelId, showAlert]);

  // 🔹 Launch Embedded Signup
  const launchSignup = useCallback(() => {
    if (!window.FB) {
      showAlert('fail', 'Facebook SDK not loaded yet. Please wait or refresh.');
      return;
    }
    if (!data) {
      showAlert('fail', 'Facebook configuration not loaded yet.');
      return;
    }

    window.FB.login((response) => {}, {
      config_id: data.configId,
      response_type: 'code',
      override_default_response_type: true,
    });
  }, [data, showAlert]);

  return { launchSignup };
};
